// OrderListSidebar.js
import React, { useState, useMemo } from 'react';
import {
   Box,
   Card,
   CardContent,
   Typography,
   TextField,
   InputAdornment,
   List,
   ListItem,
   ListItemButton,
   ListItemText,
   ListItemIcon,
   Divider,
   Button,
   CircularProgress, Tooltip, Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { DriveEta, Layers, DirectionsBoat, Info } from '@mui/icons-material';
import useSummary from "../../lib/hooks/useSummary";
import {getSubdomainClientHost} from "../../lib/constants/constants";
import {prettyDateTime, toCapitalizeFromUnderScore, truncate} from "../../lib/helpers/helpers";
import {useSearchParams} from "react-router-dom"; // adjust path if needed

export const OrderListSidebar = () => {
   const [searchParams, setSearchParams] = useSearchParams();
   const selectedOrderId = useMemo(() => searchParams.get("order"), [searchParams]);   // get first query‐param value (no hard-coded key)

   const [searchQuery, setSearchQuery] = useState('');
   const { data, isLoading, isRefetching, refetch } = useSummary();
   
   const orders = data?.orders?.filter(order => order.id != null) || [];
   
   const filteredOrders = useMemo(() => {
      const q = searchQuery.toLowerCase();
      const filtered = orders.filter(order => {
         if (
            order.name?.toLowerCase().includes(q) ||
            order.type?.toLowerCase().includes(q) ||
            order.id?.toLowerCase().includes(q)
         ) {
            return true;
         }
         const vehicles = order.details?.vehicle || [];
         return vehicles.some(({ make, model, year, vin, bin, vehicle_type }) =>
            [make, model, year?.toString(), vin, bin, vehicle_type]
               .some(field => field?.toLowerCase().includes(q))
         );
      });
      
      return filtered.sort((a, b) => {
         const updatedA = new Date(a.updated_at).getTime() || 0;
         const updatedB = new Date(b.updated_at).getTime() || 0;
         if (updatedB !== updatedA) {
            return updatedB - updatedA; // updated_at newest first
         }
         const createdA = new Date(a.created_at).getTime() || 0;
         const createdB = new Date(b.created_at).getTime() || 0;
         return createdB - createdA; // created_at newest first if updated_at equal
      });
   }, [orders, searchQuery]);
   
   const getIconForType = (type) => {
      switch (type) {
         case 'auto':
            return <DriveEta />;
         case 'material':
            return <Layers />;
         case 'marine':
            return <DirectionsBoat />;
         default:
            return <Info />;
      }
   };
   
   const handleNewQuote = () => {
      window.location.href = `${getSubdomainClientHost("quote")}/quote`;
   };
   
   const handleSelectOrder = (orderId) => {
      setSearchParams({ order: orderId });
   };
   
   return (
      <Card
            sx={{
               flexShrink: 0,
               margin: 'auto',
               width: { xs: '100%', sm: '96%', md: 280 },
               height: { xs: 400, sm: '100%', md: '100%' },
            }}>
         <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
               <Typography variant="h6">Orders</Typography>
               <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNewQuote}
               >
                  New Quote
               </Button>
            </Box>
            <TextField
               fullWidth
               size="small"
               placeholder="Search orders..."
               value={searchQuery}
               onChange={(e) => setSearchQuery(e.target.value)}
               InputProps={{
                  startAdornment: (
                     <InputAdornment position="start">
                        <SearchIcon />
                     </InputAdornment>
                  ),
               }}
               sx={{ mb: 2 }}
            />
            <Divider />
            {isLoading || isRefetching ? (
               <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
                  <CircularProgress size={28} />
               </Box>
            ) : (
               <List dense sx={{ height: '100%', overflowY: 'auto' }}>
                  {filteredOrders.map((order) => {
                     const isActive = String(order.id) === String(selectedOrderId);
                     return (
                        <ListItem key={order.id} disablePadding>
                           <ListItemButton
                              onClick={() => handleSelectOrder?.(order.id)}
                              sx={{
                                 borderRadius: 2,
                                 width: '100%',
                                 bgcolor: isActive ? 'action.hover' : 'transparent',
                                 color: isActive ? 'inherit' : 'inherit',
                                 '&:hover': {
                                    bgcolor: isActive ? 'action.hover' : 'action.hover',
                                 },
                              }}
                           >
                              <ListItemIcon>{getIconForType(order.type)}</ListItemIcon>
                              <Tooltip title={`Order: ${order.id}`} arrow>
                                 <Stack spacing={0.25}>
                                    <Typography variant="body2">
                                       {`Order: ${truncate(order.id, 8)}`}
                                    </Typography>
                                    
                                    {order.details?.vehicle?.map(({ make, model, year, vin, bin, vehicle_type }, idx) => (
                                       <Typography key={idx} variant="caption">
                                          {[year, make, model].every(Boolean)
                                             ? `${year} ${make} ${model}`
                                             : toCapitalizeFromUnderScore(vehicle_type)}
                                          <br />
                                          VIN: {vin || bin}
                                       </Typography>
                                    ))}
                                    
                                    {order.details?.materialType && (
                                       <Typography variant="caption">
                                          Material: {toCapitalizeFromUnderScore(order.details.materialType)}
                                       </Typography>
                                    )}
                                    
                                    {order.details?.vesselType && (
                                       <Typography variant="caption">
                                          Vessel: {toCapitalizeFromUnderScore(order.details.vesselType)}
                                       </Typography>
                                    )}
                                    
                                    {order.status && (
                                       <Typography variant="caption">
                                          Status: {toCapitalizeFromUnderScore(order.status)}
                                       </Typography>
                                    )}
                                    {order.updated_at && (
                                       <Typography variant="caption">
                                          {prettyDateTime(order.updated_at)}
                                       </Typography>
                                    )}
                                 </Stack>
                              </Tooltip>
                           </ListItemButton>
                        </ListItem>
                     );
                  })}
                  {filteredOrders.length === 0 && (
                     <ListItem>
                        <ListItemText primary="No results found." />
                     </ListItem>
                  )}
               </List>
            )}
         </CardContent>
      </Card>
   );
};
